import { type InfiniteData, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";

import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import type { ConversationParticipant } from "common/types";

import getQueryKeys from "./getQueryKeys";

import type { Conversation } from "../../pages/inbox/types";

export default function useUpdateConversationParticipant() {
  const queryClient = useQueryClient();

  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { conversationsKeys } = getQueryKeys(workspaceId);

  async function updateAllConversationParticipants(
    updateFunction: (participant: ConversationParticipant) => void,
  ) {
    if (queryClient.getQueriesData({ queryKey: conversationsKeys.list() })) {
      await queryClient.cancelQueries({
        queryKey: conversationsKeys.list(),
      });
      queryClient.setQueriesData<InfiniteData<Conversation[]>>(
        { queryKey: conversationsKeys.list() },
        (prevConversations) => {
          if (prevConversations) {
            return produce(prevConversations, (draftState) => {
              for (let i = 0; i < draftState.pages.length; i += 1) {
                for (let j = 0; j < draftState.pages[i].length; j += 1) {
                  for (
                    let k = 0;
                    k < draftState.pages[i][j].participants.length;
                    k += 1
                  ) {
                    const lead = draftState.pages[i][j].participants[k];
                    updateFunction(lead);
                  }
                }
              }
            });
          }
          return undefined;
        },
      );
    }
  }
  async function updateConversationParticipant(
    participantId: string,
    updateFunction: (participant: ConversationParticipant) => void,
  ) {
    if (queryClient.getQueriesData({ queryKey: conversationsKeys.list() })) {
      await queryClient.cancelQueries({ queryKey: conversationsKeys.list() });
      queryClient.setQueriesData<InfiniteData<Conversation[]>>(
        { queryKey: conversationsKeys.list() },
        (prevConversations) => {
          // prevConversations can be undefined if it hasn't loaded yet
          if (prevConversations) {
            return produce(prevConversations, (draftState) => {
              for (let i = 0; i < draftState.pages.length; i += 1) {
                for (let j = 0; j < draftState.pages[i].length; j += 1) {
                  for (
                    let k = 0;
                    k < draftState.pages[i][j].participants.length;
                    k += 1
                  ) {
                    const participant = draftState.pages[i][j].participants[k];

                    if (String(participant.id) === participantId) {
                      updateFunction(participant);
                    }
                  }
                }
              }
            });
          }
        },
      );
    }
  }
  return { updateAllConversationParticipants, updateConversationParticipant };
}
